import { Glue42Core } from "@glue42/core";
import { CallbackRegistry, UnsubscribeFunction, default as CallbackFactory } from "callback-registry";
import { Glue42 } from "../../glue";
import { Environment, GroupsWrapper, WindowWrapper } from "./types";
import jumpListManager from "./jumpList/manager";

import { default as envDetector } from "./environment/detector";
import groupsFactory from "./groups";
import { default as windowStore } from "./store";
import executor from "./environment/gd/executor";
import * as util from "../activity/helpers/util";
import utils from "../utils";

export default (agm: Glue42Core.AGM.API, logger: Glue42Core.Logger.API, appManagerGetter: () => Glue42.AppManager.API, displayAPIGetter: () => Glue42.Displays.API, channelsGetter: () => Glue42.Channels, gdMajorVersion: number): Glue42.Windows.API => {
    const _registry: CallbackRegistry = CallbackFactory();
    const _logger = logger;
    let groups: GroupsWrapper;
    let environment: Environment;

    windowStore.init(_logger);

    const isReady = new Promise<void>((resolve, reject) => {
        envDetector(agm, _logger, appManagerGetter, displayAPIGetter, channelsGetter, gdMajorVersion)
            .then((env) => {
                environment = env;
                groups = groupsFactory(env, _logger);
                jumpListManager.init(env.executor, agm, _logger);
                resolve();
            })
            .catch((e: Error) => {
                const err = `Timed out waiting for connection to Glue42 Enterprise: Error: ${e.message}`;
                _logger.error(err, e);
                reject(new Error(err));
            });
    });

    function ready() {
        return isReady;
    }

    function my(): Glue42.Windows.GDWindow {
        const myWindow = windowStore.getIfReady(environment.my());
        return myWindow ? myWindow.API : undefined;
    }

    function open(name: string, url: string, options?: Glue42.Windows.WindowSettings, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            if (util.isNullOrWhiteSpace(name)) {
                throw new Error("The window name is missing.");
            }
            if (util.isNullOrWhiteSpace(url)) {
                throw new Error("The window URL is missing.");
            }

            if (!util.isUndefinedOrNull(options)) {
                const optionsAsAny = options as any;
                for (const prop of ["minHeight", "maxHeight", "minWidth", "maxWidth", "width", "height", "top", "left"]) {
                    if (prop in optionsAsAny) {
                        const value = optionsAsAny[prop];
                        if (util.isUndefinedOrNull(value)) {
                            delete optionsAsAny[prop];
                            continue;
                        }
                        if (!util.isNumber(value)) {
                            const errMessage = `${prop} must be a number`;
                            throw new Error(errMessage);
                        }
                        if (optionsAsAny[prop] === "width" || optionsAsAny[prop] === "height") {
                            if (value <= 0) {
                                const errMessage = `${prop} must be a positive number`;
                                throw new Error(errMessage);
                            }
                        }
                    }
                }
            }
            return environment.open(name, url, options);
        }, success, error);
    }

    function find(name: string, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Glue42.Windows.GDWindow {
        const windows: { [key: string]: WindowWrapper } = windowStore.list;

        const windowsForListing = Object.keys(windows).reduce<Glue42.Windows.GDWindow[]>((memo, winId) => {
            const window: WindowWrapper = windows[winId];
            if (window?.API?.name === name) {
                memo.push(window.API);
            }
            return memo;
        }, []);

        const win = windowsForListing[0];
        if (win) {
            if (typeof success === "function") {
                success(windowsForListing[0]);
            }
            return windowsForListing[0];
        } else {
            if (typeof error === "function") {
                error("There is no window with name:" + name);
            }
        }
    }

    function findById(id: string, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Glue42.Windows.GDWindow {
        const windows: { [key: string]: WindowWrapper } = windowStore.list;
        const windowsForListing = Object.keys(windows).reduce<Glue42.Windows.GDWindow[]>((memo, winId) => {
            const window: WindowWrapper = windows[winId];
            if (typeof window !== "undefined" && window.API.id === id) {
                memo.push(window.API);
            }
            return memo;
        }, []);
        const win = windowsForListing[0];
        if (win) {
            if (typeof success === "function") {
                success(windowsForListing[0]);
            }
            return windowsForListing[0];
        } else {
            if (typeof error === "function") {
                error("There is no window with such id:" + id);
            }
        }
    }

    function list(success?: (window: Glue42.Windows.GDWindow[]) => void): Glue42.Windows.GDWindow[] {
        const windows: { [key: string]: WindowWrapper } = windowStore.list;

        const windowsForListing: Glue42.Windows.GDWindow[] = Object.keys(windows)
            .map((k) => {
                return windows[k].API;
            });

        if (typeof success !== "function") {
            return windowsForListing;
        }

        success(windowsForListing);
    }

    function configure(options: Glue42.Windows.WindowsConfiguration): Promise<void> {
        const win = my();
        const winId = win ? win.id : "";
        return executor.configure(winId, options);
    }

    function windowAdded(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return _registry.add("window-added", callback);
    }

    function windowRemoved(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return _registry.add("window-removed", callback);
    }

    function tabAttached(callback: (tabWindow: Glue42.Windows.GDWindow, newTabGroupId: string, tabHeaderVisible: boolean) => void): UnsubscribeFunction {
        let unsubFunc: UnsubscribeFunction;
        let unsubscribed = false;
        isReady.then(() => {
            if (unsubscribed) {
                return;
            }
            unsubFunc = environment.tabAttached(callback);
        });

        return () => {
            unsubscribed = true;
            if (unsubFunc) {
                unsubFunc();
            }
        };
    }

    function tabDetached(callback: (window: Glue42.Windows.GDWindow, tabGroupId: string, oldTabGroupId: string) => void): UnsubscribeFunction {
        let unsubFunc: UnsubscribeFunction;
        let unsubscribed = false;
        isReady.then(() => {
            if (unsubscribed) {
                return;
            }
            unsubFunc = environment.tabDetached(callback);
        });

        return () => {
            unsubscribed = true;
            if (unsubFunc) {
                unsubFunc();
            }
        };
    }

    function onWindowFrameColorChanged(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        let unsubFunc: UnsubscribeFunction;
        let unsubscribed = false;
        isReady.then(() => {
            if (unsubscribed) {
                return;
            }
            unsubFunc = environment.onWindowFrameColorChanged(callback);
        });

        return () => {
            unsubscribed = true;
            if (unsubFunc) {
                unsubFunc();
            }
        };
    }

    function onWindowGotFocus(callback: (window: Glue42.Windows.GDWindow) => void) {
        let unsubFunc: UnsubscribeFunction;
        let unsubscribed = false;
        isReady.then(() => {
            if (unsubscribed) {
                return;
            }
            unsubFunc = environment.onWindowGotFocus(callback);
        });

        return () => {
            unsubscribed = true;
            if (unsubFunc) {
                unsubFunc();
            }
        };
    }

    function onWindowLostFocus(callback: (window: Glue42.Windows.GDWindow) => void) {
        let unsubFunc: UnsubscribeFunction;
        let unsubscribed = false;
        isReady.then(() => {
            if (unsubscribed) {
                return;
            }
            unsubFunc = environment.onWindowLostFocus(callback);
        });

        return () => {
            unsubscribed = true;
            if (unsubFunc) {
                unsubFunc();
            }
        };
    }

    function onEvent(callback: (stream: any) => void): UnsubscribeFunction {
        let unsubFunc: UnsubscribeFunction;
        let unsubscribed = false;
        isReady.then(() => {
            if (unsubscribed) {
                return;
            }
            unsubFunc = environment.onEvent(callback);
        });

        return () => {
            unsubscribed = true;
            if (unsubFunc) {
                unsubFunc();
            }
        };
    }

    function createFlydown(targetId: string, config: Glue42.Windows.FlydownOptions): Promise<Glue42.Windows.Flydown> {
        return environment.createFlydown(targetId, config);
    }

    function showPopup(targetId: string, config: Glue42.Windows.PopupOptions): Promise<void> {
        return environment.showPopup(targetId, config);
    }

    function handleWindowAdded(w: WindowWrapper) {
        _registry.execute("window-added", w.API);
    }

    function handleWindowRemoved(w: WindowWrapper) {
        _registry.execute("window-removed", w.API);
    }

    windowStore.onReadyWindow(handleWindowAdded);
    windowStore.onRemoved(handleWindowRemoved);

    // The API itself
    return {
        my,
        open,
        find,
        findById,
        list,
        ready,
        onWindowAdded: windowAdded,
        windowAdded,
        onWindowRemoved: windowRemoved,
        windowRemoved,
        onTabAttached: tabAttached,
        onTabDetached: tabDetached,
        onWindowFrameColorChanged,

        get groups() {
            return groups.groupsAPI;
        },
        onWindowGotFocus,
        onWindowLostFocus,
        onEvent,
        createFlydown,
        showPopup,
        configure
    };
};
